import { createContext, useContext, useState, useCallback } from 'react';
import {
  Snackbar,
  Alert,
  AlertTitle,
  Slide
} from '@mui/material';

const ToastContext = createContext();

/**
 * Transition components for toast animations
 */
function SlideDownTransition(props) {
  return <Slide {...props} direction="down" />;
}

// #1577: bottom-anchored toasts rise from beneath - they sit above an open
// bottom Sheet, so upward motion matches their placement.
function SlideUpTransition(props) {
  return <Slide {...props} direction="up" />;
}

/**
 * Toast notification provider
 */
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((message, severity = 'info', options = {}) => {
    // For non-error toasts, log to console instead of showing Snackbar.
    // `visible: true` is the one opt-in (DESIGN_SYSTEM.md 8.10): a brief
    // confirmation the user needs, such as the transfer mirror (#1990).
    if ((severity === 'success' || severity === 'info' || severity === 'warning') && !options.visible) {
      console.log(`[${severity.toUpperCase()}] ${message}`);
      return null;
    }

    // For error toasts, render Snackbar at top of screen
    const id = Date.now() + Math.random();
    const toast = {
      id,
      message,
      severity,
      title: options.title,
      // Error toasts 6 seconds; an opted-in confirmation is brief.
      duration: options.duration || (severity === 'error' ? 6000 : 3000),
      ...options,
      // #1577: 'bottom' anchors the toast above a bottom Sheet; anything
      // else keeps the classic top-center stack. bottomOffset = px from the
      // viewport bottom to the first toast (callers measure the open sheet).
      anchor: options.anchor === 'bottom' ? 'bottom' : 'top',
      bottomOffset: options.bottomOffset ?? 16
    };

    setToasts(prev => [...prev, toast]);

    // Auto-remove toast after duration
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, toast.duration);

    return id;
  }, []);

  const hideToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const showSuccess = useCallback((message, options = {}) => {
    return showToast(message, 'success', options);
  }, [showToast]);

  const showError = useCallback((message, options = {}) => {
    return showToast(message, 'error', options);
  }, [showToast]);

  const showWarning = useCallback((message, options = {}) => {
    return showToast(message, 'warning', options);
  }, [showToast]);

  const showInfo = useCallback((message, options = {}) => {
    return showToast(message, 'info', options);
  }, [showToast]);

  const contextValue = {
    showToast,
    hideToast,
    showSuccess,
    showError,
    showWarning,
    showInfo
  };

  // Group-local indexes keep each stack's spacing independent (#1577).
  const topToasts = toasts.filter((t) => t.anchor !== 'bottom');
  const bottomToasts = toasts.filter((t) => t.anchor === 'bottom');

  const toastAlert = (toast) => (
    <Alert
      severity={toast.severity}
      onClose={() => hideToast(toast.id)}
      variant="filled"
      sx={{ minWidth: 300 }}
    >
      {toast.title && <AlertTitle>{toast.title}</AlertTitle>}
      {toast.message}
    </Alert>
  );

  return (
    <ToastContext.Provider value={contextValue}>
      {children}

      {/* Render error toasts at top of screen (default) */}
      {topToasts.map((toast, idx) => (
        <Snackbar
          key={toast.id}
          open={true}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          TransitionComponent={SlideDownTransition}
          sx={{
            mt: idx * 7, // Stack toasts vertically from top
            zIndex: 1400 + idx // Ensure proper layering
          }}
        >
          {toastAlert(toast)}
        </Snackbar>
      ))}

      {/* #1577: bottom-anchored stack - floats above an open bottom Sheet
          (drawer zIndex 1200) and stacks upward in 56px steps, first toast
          nearest the sheet. */}
      {bottomToasts.map((toast, idx) => (
        <Snackbar
          key={toast.id}
          open={true}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          TransitionComponent={SlideUpTransition}
          sx={{
            bottom: `${toast.bottomOffset + idx * 56}px`,
            zIndex: 1400 + idx
          }}
        >
          {toastAlert(toast)}
        </Snackbar>
      ))}
    </ToastContext.Provider>
  );
};

/**
 * Hook to use toast notifications
 */
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export default ToastProvider;